import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Button from 'src/components/Button';
import Checkbox from 'src/components/Checkbox';
import Flex from 'src/components/Flex';
import Input, { Label } from 'src/components/Input';
import LoadingIcon from 'src/components/LoadingIcon';
import ModalHeader from 'src/components/ModalHeader';
import SideModal from 'src/components/SideModal';
import { ToastStoreObject, ToastType } from 'src/stores/ToastStore';
import { Facility } from 'src/stores/FacilityStore';
import * as facilitiesAPI from 'src/api/facilities';
import {
    FacilityCloneCandidates,
    FacilityCloneValueSetCandidate,
    FacilityCloneOptionCandidate,
} from 'src/api/facilities';
import { DEFAULT_FACILITY_TIME_ZONE, FACILITY_TIMEZONES } from 'src/utils/facilityTimeZones';
import { formatFacilityOptionValueForEditing, formatFacilityOptionValueForSubmit } from 'src/utils/facilityOptions';
import * as utils from 'src/utils';
import styles from './styles.module.scss';

interface FacilityCloneWizardProps {
    facility: Facility;
    facilities?: Facility[];
    onClose: Function;
    onSave: Function;
}

interface ValueSetSelection {
    selected: boolean;
    copyDetails: boolean;
}

const STEPS = ['Details', 'Forms', 'Value Sets', 'Options', 'Review'];

const EMPTY_FACILITY: Facility & { facilityTimeZone?: string } = {
    facNm: '',
    facNmIntrnl: '',
    facDesc: '',
    addrLn1: '',
    addrLn2: '',
    addrCityNm: '',
    addrStCd: '',
    addrZipCd: '',
    phNoMain: '',
    facilityTimeZone: DEFAULT_FACILITY_TIME_ZONE,
};

function FacilityCloneWizard(props: FacilityCloneWizardProps) {
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [candidates, setCandidates] = useState<FacilityCloneCandidates>(null);
    const [facility, setFacility] = useState<Facility & { facilityTimeZone?: string }>(EMPTY_FACILITY);
    const [selectedFormIds, setSelectedFormIds] = useState<number[]>([]);
    const [valueSetSelections, setValueSetSelections] = useState<Record<number, ValueSetSelection>>({});
    const [facilityOptionValues, setFacilityOptionValues] = useState<Record<string, string>>({});

    useEffect(() => {
        loadCloneCandidates();
    }, [props.facility?.facId]);

    async function loadCloneCandidates() {
        try {
            setIsLoading(true);
            const cloneCandidates = await facilitiesAPI.fetchFacilityCloneCandidates(props.facility.facId);
            setCandidates(cloneCandidates);
            setFacility(buildInitialFacility(cloneCandidates.sourceFacility));
            setSelectedFormIds(cloneCandidates.forms.map((form) => form.formDefinitionId));
            setValueSetSelections(
                cloneCandidates.valueSets.reduce<Record<number, ValueSetSelection>>((acc, valueSet) => {
                    acc[valueSet.valueSetId] = { selected: true, copyDetails: false };
                    return acc;
                }, {}),
            );
            setFacilityOptionValues(
                cloneCandidates.facilityOptions.reduce<Record<string, string>>((acc, option) => {
                    acc[option.optionName] = formatFacilityOptionValueForEditing(option);
                    return acc;
                }, {}),
            );
        } catch (error) {
            ToastStoreObject.show(utils.parseError(error), ToastType.Error);
        } finally {
            setIsLoading(false);
        }
    }

    function buildInitialFacility(sourceFacility: Facility & { facilityTimeZone?: string }) {
        return {
            facNm: '',
            facNmIntrnl: '',
            facDesc: '',
            addrLn1: sourceFacility.addrLn1,
            addrLn2: sourceFacility.addrLn2,
            addrCityNm: sourceFacility.addrCityNm,
            addrStCd: sourceFacility.addrStCd,
            addrZipCd: sourceFacility.addrZipCd,
            phNoMain: sourceFacility.phNoMain,
            testFacInd: sourceFacility.testFacInd,
            facilityTypeCode: sourceFacility.facilityTypeCode,
            facilityNumber: sourceFacility.facilityNumber,
            gpsLocation: sourceFacility.gpsLocation,
            nationalGridCoordinates: sourceFacility.nationalGridCoordinates,
            cityCode: sourceFacility.cityCode,
            stateCode: sourceFacility.stateCode,
            countyCode: sourceFacility.countyCode,
            countryCode: sourceFacility.countryCode,
            facilityPhones: sourceFacility.facilityPhones,
            subscriptionData: sourceFacility.subscriptionData,
            facilityTimeZone: sourceFacility.facilityTimeZone || DEFAULT_FACILITY_TIME_ZONE,
        };
    }

    function updateFacility(property: keyof Facility | 'facilityTimeZone', value: any) {
        setFacility({ ...facility, [property]: value });
    }

    function updateFacilityName(facilityName: string) {
        setFacility({
            ...facility,
            facNm: facilityName,
            facNmIntrnl: buildUniqueFacilityInternalName(facilityName, props.facilities || []),
        });
    }

    function updateFacilityInternalName(facilityInternalName: string) {
        setFacility({
            ...facility,
            facNmIntrnl: normalizeFacilityInternalName(facilityInternalName),
        });
    }

    function toggleForm(formDefinitionId: number, selected: boolean) {
        setSelectedFormIds((currentIds) => {
            if (selected) {
                return currentIds.includes(formDefinitionId) ? currentIds : [...currentIds, formDefinitionId];
            }
            return currentIds.filter((id) => id !== formDefinitionId);
        });
    }

    function toggleAllForms(selected: boolean) {
        setSelectedFormIds(selected ? candidates.forms.map((form) => form.formDefinitionId) : []);
    }

    function getRequiredStaticValueSetNames(selections: Record<number, ValueSetSelection> = valueSetSelections) {
        const requiredNames = new Set<string>();
        candidates?.valueSets
            .filter((valueSet) => valueSet.valueSetType === 'DYNAMIC' && selections[valueSet.valueSetId]?.selected)
            .forEach((valueSet) => {
                (valueSet.dynamicDependencies || []).forEach((dependency) => requiredNames.add(dependency));
            });
        return requiredNames;
    }

    function toggleValueSet(valueSet: FacilityCloneValueSetCandidate, selected: boolean) {
        const nextSelections = {
            ...valueSetSelections,
            [valueSet.valueSetId]: {
                ...(valueSetSelections[valueSet.valueSetId] || { copyDetails: false }),
                selected,
            },
        };

        if (!selected && valueSet.valueSetType === 'STATIC' && isRequiredStaticValueSet(valueSet)) {
            return;
        }

        if (selected && valueSet.valueSetType === 'DYNAMIC') {
            (valueSet.dynamicDependencies || []).forEach((dependencyName) => {
                const dependency = candidates.valueSets.find(
                    (candidate) =>
                        candidate.valueSetType === 'STATIC' && candidate.valueSetName === dependencyName,
                );
                if (dependency) {
                    nextSelections[dependency.valueSetId] = {
                        ...(nextSelections[dependency.valueSetId] || { copyDetails: false }),
                        selected: true,
                    };
                }
            });
        }

        setValueSetSelections(nextSelections);
    }

    function toggleAllValueSets(selected: boolean) {
        setValueSetSelections(
            candidates.valueSets.reduce<Record<number, ValueSetSelection>>((acc, valueSet) => {
                acc[valueSet.valueSetId] = {
                    selected,
                    copyDetails: valueSetSelections[valueSet.valueSetId]?.copyDetails === true,
                };
                return acc;
            }, {}),
        );
    }

    function toggleCopyDetails(valueSetId: number, copyDetails: boolean) {
        setValueSetSelections({
            ...valueSetSelections,
            [valueSetId]: {
                ...(valueSetSelections[valueSetId] || { selected: false }),
                copyDetails,
            },
        });
    }

    function isRequiredStaticValueSet(valueSet: FacilityCloneValueSetCandidate) {
        return valueSet.valueSetType === 'STATIC' && getRequiredStaticValueSetNames().has(valueSet.valueSetName);
    }

    function updateFacilityOption(optionName: string, optionValue: string) {
        setFacilityOptionValues({ ...facilityOptionValues, [optionName]: optionValue });
    }

    function canProceed() {
        if (currentStep === 0) {
            return (
                !!String(facility.facNm || '').trim() &&
                !!String(facility.facNmIntrnl || '').trim() &&
                !!facility.facilityTimeZone &&
                !getFacilityNameError() &&
                !getFacilityInternalNameError()
            );
        }
        return true;
    }

    async function handleSubmit() {
        try {
            setIsSubmitting(true);
            await facilitiesAPI.cloneFacility(props.facility.facId, {
                facility: buildFacilityPayload(),
                formDefinitionIds: selectedFormIds,
                valueSets: getSelectedValueSetPayload(),
                facilityOptions: getFacilityOptionPayload(),
            });
            ToastStoreObject.show('Facility cloned successfully', ToastType.Success);
            props.onSave();
            props.onClose();
        } catch (error) {
            ToastStoreObject.show(utils.parseError(error), ToastType.Error);
        } finally {
            setIsSubmitting(false);
        }
    }

    function buildFacilityPayload(): Facility {
        return {
            facNm: String(facility.facNm || '').trim(),
            facNmIntrnl: normalizeFacilityInternalName(facility.facNmIntrnl),
            facDesc: facility.facDesc,
            addrLn1: facility.addrLn1,
            addrLn2: facility.addrLn2,
            addrCityNm: facility.addrCityNm,
            addrStCd: facility.addrStCd,
            addrZipCd: facility.addrZipCd,
            phNoMain: facility.phNoMain,
            testFacInd: facility.testFacInd,
            facilityTypeCode: facility.facilityTypeCode,
            facilityNumber: facility.facilityNumber,
            gpsLocation: facility.gpsLocation,
            nationalGridCoordinates: facility.nationalGridCoordinates,
            cityCode: facility.cityCode,
            stateCode: facility.stateCode,
            countyCode: facility.countyCode,
            countryCode: facility.countryCode,
            facilityPhones: facility.facilityPhones,
            subscriptionData: facility.subscriptionData,
            facilityTimeZone: facility.facilityTimeZone as any,
        };
    }

    function getSelectedValueSetPayload() {
        return Object.entries(valueSetSelections)
            .filter(([_, selection]) => selection.selected)
            .map(([valueSetId, selection]) => ({
                valueSetId: parseInt(valueSetId),
                copyDetails: selection.copyDetails,
            }));
    }

    function getSelectedValueSets() {
        return candidates.valueSets.filter((valueSet) => valueSetSelections[valueSet.valueSetId]?.selected);
    }

    function getCopyDetailsCount() {
        return getSelectedValueSets().filter(
            (valueSet) => valueSet.valueSetType === 'STATIC' && valueSetSelections[valueSet.valueSetId]?.copyDetails,
        ).length;
    }

    function getFacilityOptionPayload() {
        return candidates.facilityOptions.map((option) => ({
            optionName: option.optionName,
            optionValue: formatFacilityOptionValueForSubmit(option, facilityOptionValues[option.optionName] || ''),
        }));
    }

    function getFacilityNameError() {
        const facilityName = String(facility.facNm || '').trim().toLowerCase();
        if (!facilityName) {
            return '';
        }
        return (props.facilities || []).some(
            (existingFacility) => String(existingFacility.facNm || '').trim().toLowerCase() === facilityName,
        )
            ? 'Facility name already exists'
            : '';
    }

    function getFacilityInternalNameError() {
        const facilityInternalName = normalizeFacilityInternalName(facility.facNmIntrnl);
        if (!facilityInternalName) {
            return '';
        }
        return (props.facilities || []).some(
            (existingFacility) => normalizeFacilityInternalName(existingFacility.facNmIntrnl) === facilityInternalName,
        )
            ? 'Internal name already exists'
            : '';
    }

    function renderSteps() {
        return (
            <div className={styles.steps}>
                {STEPS.map((step, index) => (
                    <button
                        key={step}
                        className={`${styles.step} ${index === currentStep ? styles.activeStep : ''}`}
                        onClick={() => index < currentStep && setCurrentStep(index)}
                        disabled={index > currentStep}
                    >
                        <span>{index + 1}</span>
                        {step}
                    </button>
                ))}
            </div>
        );
    }

    function renderDetailsStep() {
        const selectedTimeZone = FACILITY_TIMEZONES.find((timezone) => timezone.value === facility.facilityTimeZone);
        return (
            <div className={styles.stepContent}>
                <div className={styles.sourceSummary}>
                    Source facility: <strong>{candidates.sourceFacility.facNm}</strong>
                </div>
                <div className={styles.grid}>
                    <Input
                        label="Name"
                        required
                        value={facility.facNm}
                        errorMessage={getFacilityNameError()}
                        onChangeText={(value: string) => updateFacilityName(value)}
                    />
                    <Input
                        label="Internal Name"
                        required
                        value={facility.facNmIntrnl as any}
                        errorMessage={getFacilityInternalNameError()}
                        onChangeText={(value: string) => updateFacilityInternalName(value)}
                    />
                </div>
                <Input
                    label="Description"
                    value={facility.facDesc}
                    onChangeText={(value: string) => updateFacility('facDesc', value)}
                />
                <div className={styles.grid}>
                    <Input
                        label="Address 1"
                        value={facility.addrLn1}
                        onChangeText={(value: string) => updateFacility('addrLn1', value)}
                    />
                    <Input
                        label="Address 2"
                        value={facility.addrLn2}
                        onChangeText={(value: string) => updateFacility('addrLn2', value)}
                    />
                    <Input
                        label="City"
                        value={facility.addrCityNm}
                        onChangeText={(value: string) => updateFacility('addrCityNm', value)}
                    />
                    <Input
                        label="State"
                        value={facility.addrStCd}
                        onChangeText={(value: string) => updateFacility('addrStCd', value)}
                    />
                    <Input
                        label="Zip"
                        value={facility.addrZipCd}
                        onChangeText={(value: string) => updateFacility('addrZipCd', value)}
                    />
                    <Input
                        label="Phone"
                        value={facility.phNoMain}
                        onChangeText={(value: string) => updateFacility('phNoMain', value)}
                    />
                </div>
                <Label text="Time Zone" required className={styles.label} />
                <Select
                    value={selectedTimeZone}
                    placeholder="Select Time Zone"
                    onChange={(option: any) => updateFacility('facilityTimeZone', option.value)}
                    options={FACILITY_TIMEZONES}
                    styles={utils.styleSelectComponent(true)}
                    menuPortalTarget={document.body}
                    isSearchable
                />
            </div>
        );
    }

    function renderFormsStep() {
        return (
            <div className={styles.stepContent}>
                <SelectionHeader
                    title="Forms"
                    selectedCount={selectedFormIds.length}
                    totalCount={candidates.forms.length}
                    onSelectAll={() => toggleAllForms(true)}
                    onSelectNone={() => toggleAllForms(false)}
                />
                <div className={styles.table}>
                    <div className={styles.tableHeader}>
                        <span />
                        <span>Name</span>
                        <span>Type</span>
                        <span>Version</span>
                        <span>Pages</span>
                    </div>
                    {candidates.forms.map((form) => (
                        <div key={form.formDefinitionId} className={styles.tableRow}>
                            <Checkbox
                                checked={selectedFormIds.includes(form.formDefinitionId)}
                                onChange={(event) => toggleForm(form.formDefinitionId, event.target.checked)}
                            />
                            <span>
                                <strong>{form.formDefinitionTitle}</strong>
                                <small>{form.formDefinitionName}</small>
                            </span>
                            <span>{form.formDefinitionType}</span>
                            <span>{form.formDefinitionVersion}</span>
                            <span>{form.pageCount}</span>
                        </div>
                    ))}
                    {!candidates.forms.length && <div className={styles.empty}>No active forms found.</div>}
                </div>
            </div>
        );
    }

    function renderValueSetsStep() {
        return (
            <div className={styles.stepContent}>
                <SelectionHeader
                    title="Value Sets"
                    selectedCount={getSelectedValueSets().length}
                    totalCount={candidates.valueSets.length}
                    onSelectAll={() => toggleAllValueSets(true)}
                    onSelectNone={() => toggleAllValueSets(false)}
                />
                <div className={styles.table}>
                    <div className={styles.valueSetHeader}>
                        <span />
                        <span>Name</span>
                        <span>Type</span>
                        <span>Values</span>
                        <span>Copy Data</span>
                    </div>
                    {candidates.valueSets.map((valueSet) => {
                        const selection = valueSetSelections[valueSet.valueSetId] || {
                            selected: false,
                            copyDetails: false,
                        };
                        const requiredStatic = isRequiredStaticValueSet(valueSet);
                        return (
                            <div key={valueSet.valueSetId} className={styles.valueSetRow}>
                                <Checkbox
                                    checked={selection.selected}
                                    disabled={requiredStatic}
                                    onChange={(event) => toggleValueSet(valueSet, event.target.checked)}
                                />
                                <span>
                                    <strong>{valueSet.valueSetName}</strong>
                                    <small>{valueSet.valueSetDescription}</small>
                                    {requiredStatic && <small>Required by a selected dynamic value set</small>}
                                </span>
                                <span>{valueSet.valueSetType}</span>
                                <span>{valueSet.detailCount}</span>
                                <span>
                                    {valueSet.valueSetType === 'STATIC' ? (
                                        <Checkbox
                                            checked={selection.copyDetails}
                                            disabled={!selection.selected}
                                            onChange={(event) =>
                                                toggleCopyDetails(valueSet.valueSetId, event.target.checked)
                                            }
                                        />
                                    ) : (
                                        <span className={styles.muted}>Structure only</span>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                    {!candidates.valueSets.length && <div className={styles.empty}>No facility value sets found.</div>}
                </div>
            </div>
        );
    }

    function renderOptionsStep() {
        return (
            <div className={styles.stepContent}>
                <div className={styles.selectionHeader}>
                    <h3>Facility Settings</h3>
                </div>
                <div className={styles.table}>
                    <div className={styles.optionHeader}>
                        <span>Setting</span>
                        <span>Value</span>
                    </div>
                    {candidates.facilityOptions.map((option) => {
                        const optionValue = facilityOptionValues[option.optionName] || '';
                        return (
                            <div key={option.optionName} className={styles.optionRow}>
                                <span>
                                    <strong>{option.optionDisplayName}</strong>
                                    <small>{option.optionDescription}</small>
                                </span>
                                {renderFacilityOptionInput(option, optionValue)}
                            </div>
                        );
                    })}
                    {!candidates.facilityOptions.length && (
                        <div className={styles.empty}>No configurable facility settings found.</div>
                    )}
                </div>
            </div>
        );
    }

    function renderFacilityOptionInput(option: FacilityCloneOptionCandidate, optionValue: string) {
        if (option.inputType === 'boolean') {
            return (
                <Checkbox
                    checked={optionValue.toLowerCase() === 'true'}
                    onChange={(event) =>
                        updateFacilityOption(option.optionName, event.target.checked ? 'true' : 'false')
                    }
                />
            );
        }

        if (option.inputType === 'email-list') {
            return (
                <Input
                    multiLine
                    rows={4}
                    value={optionValue}
                    onChangeText={(value: string) => updateFacilityOption(option.optionName, value)}
                />
            );
        }

        return (
            <Input
                type={option.inputType === 'date' ? 'date' : 'text'}
                value={optionValue}
                placeholder={option.inputType === 'datetime' ? 'YYYY-MM-DD HH:mm:ss' : ''}
                onChangeText={(value: string) => updateFacilityOption(option.optionName, value)}
            />
        );
    }

    function renderReviewStep() {
        return (
            <div className={styles.stepContent}>
                <div className={styles.reviewGrid}>
                    <ReviewItem label="New Facility" value={facility.facNm} />
                    <ReviewItem label="Internal Name" value={facility.facNmIntrnl as any} />
                    <ReviewItem label="Time Zone" value={facility.facilityTimeZone} />
                    <ReviewItem label="Forms" value={selectedFormIds.length.toString()} />
                    <ReviewItem label="Value Sets" value={getSelectedValueSets().length.toString()} />
                    <ReviewItem label="Value Sets With Data" value={getCopyDetailsCount().toString()} />
                    <ReviewItem label="Settings" value={candidates.facilityOptions.length.toString()} />
                </div>
            </div>
        );
    }

    function renderCurrentStep() {
        if (!candidates) {
            return null;
        }
        switch (currentStep) {
            case 0:
                return renderDetailsStep();
            case 1:
                return renderFormsStep();
            case 2:
                return renderValueSetsStep();
            case 3:
                return renderOptionsStep();
            case 4:
                return renderReviewStep();
            default:
                return null;
        }
    }

    return (
        <SideModal isOpen={true} isWide onClose={() => props.onClose()}>
            <ModalHeader title="Clone Facility" onClose={() => props.onClose()} />
            <div className={styles.contentWrap}>
                {isLoading ? (
                    <Flex align="center" justify="center" className={styles.loading}>
                        <LoadingIcon />
                    </Flex>
                ) : (
                    <>
                        {renderSteps()}
                        {renderCurrentStep()}
                    </>
                )}
            </div>
            <div className={styles.footer}>
                <Button
                    type="outline"
                    text={currentStep === 0 ? 'Cancel' : 'Back'}
                    onClick={() => (currentStep === 0 ? props.onClose() : setCurrentStep(currentStep - 1))}
                    disabled={isSubmitting}
                />
                {currentStep < STEPS.length - 1 ? (
                    <Button
                        type="primary"
                        text="Next"
                        onClick={() => setCurrentStep(currentStep + 1)}
                        disabled={!canProceed()}
                    />
                ) : (
                    <Button
                        type="primary"
                        text="Clone Facility"
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                        disabled={!canProceed()}
                    />
                )}
            </div>
        </SideModal>
    );
}

function SelectionHeader({
    title,
    selectedCount,
    totalCount,
    onSelectAll,
    onSelectNone,
}: {
    title: string;
    selectedCount: number;
    totalCount: number;
    onSelectAll: Function;
    onSelectNone: Function;
}) {
    return (
        <Flex align="center" className={styles.selectionHeader}>
            <div>
                <h3>{title}</h3>
                <span>
                    {selectedCount} of {totalCount} selected
                </span>
            </div>
            <Flex value={1} />
            <Button type="secondarySmall" text="All" onClick={() => onSelectAll()} />
            <Button type="secondarySmall" text="None" onClick={() => onSelectNone()} />
        </Flex>
    );
}

function ReviewItem({ label, value }: { label: string; value: string }) {
    return (
        <div className={styles.reviewItem}>
            <span>{label}</span>
            <strong>{value || '-'}</strong>
        </div>
    );
}

function buildUniqueFacilityInternalName(facilityName: string, facilities: Facility[]) {
    const baseInternalName = buildFacilityInternalName(facilityName);
    if (!baseInternalName) {
        return '';
    }

    const existingInternalNames = new Set(
        (facilities || []).map((facility) => normalizeFacilityInternalName(facility.facNmIntrnl)).filter(Boolean),
    );
    let candidate = baseInternalName;
    let suffix = 2;
    while (existingInternalNames.has(candidate)) {
        candidate = `${baseInternalName}${suffix}`;
        suffix += 1;
    }
    return candidate;
}

function buildFacilityInternalName(facilityName: string) {
    const words = String(facilityName || '').match(/[a-zA-Z0-9]+/g) || [];
    if (!words.length) {
        return '';
    }

    const baseInternalName =
        words.length === 1 ? words[0].slice(0, 5) : words.map((word) => word.charAt(0)).join('');
    return normalizeFacilityInternalName(baseInternalName);
}

function normalizeFacilityInternalName(value: unknown) {
    return String(value || '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase();
}

export default FacilityCloneWizard;
